import { Only, Partial, SafeIterable } from "@pdfking/tfunc"

import type { Executor } from "#public"
import type { PlanFunction } from "#public"
import type { TaskDescriptor } from "#public"
import type { TaskImplementations, TaskMap } from "#public"
import type { TrackingCallback, TrackingStatus } from "#public"

import { TaskIO } from "../types/TaskIO"
import { TrackingEnum } from "../types/Tracking"
import { CreatePlan } from "./CreatePlan"

const is_atom = (node: PlanFunction<unknown>): boolean =>
  node.outputIO === TaskIO.ATOM && (node.inputIO !== TaskIO.ATOM || node.inputs.every(is_atom))

export const CreateExecutor = <TM extends TaskMap, C = unknown>(
  tasks: TaskImplementations<TM, C>,
  descriptor: TaskDescriptor,
  tracking?: TrackingCallback
): Executor<TM, C> => {
  const plan = CreatePlan<TM>(descriptor)

  const execute = (<X>(node: PlanFunction<X>, context?: C) =>
    is_atom(node) ? Only(run(node, context)) : run(node, context)) as Executor<TM, C>["execute"]

  const run = async function* <X>(node: PlanFunction<X>, context?: C): AsyncGenerator<X> {
    const guid = node.hash
    const emit = (status: TrackingStatus) =>
      tracking?.({ enum: TrackingEnum.STATUS, guid, ...status })

    tracking?.({ enum: TrackingEnum.START, guid })

    const inputs = node.inputs.map(input => run(input, context))

    // ATOM input fans a single node out into one invoke() call per upstream
    // item (map semantics): each call needs its own wire/cache identity, or
    // CachedForward keys them all on the same node guid and replays call 1's
    // result for every later item. A task with no input is called once; a
    // STREAM task once with its input stream, a VECTOR task once with the
    // array of them, so the node guid is already unique per call.
    const results = async function* (calls: AsyncIterable<unknown[]> | Iterable<unknown[]>) {
      let index = 0
      for await (const args of calls) {
        const call_guid =
          node.inputIO === TaskIO.ATOM ? (`${guid}:${index++}` as typeof guid) : guid
        const invoke = Partial(
          tasks[node.name as keyof TM & string] as any,
          { context, emit, guid: call_guid, plan, execute },
          node.data
        )
        const result = invoke(...args)
        if (node.outputIO === TaskIO.ATOM) {
          yield (await result) as X
        } else {
          yield* result as AsyncGenerator<X>
        }
      }
    }

    const calls =
      node.inputIO === TaskIO.VECTOR
        ? [[inputs]]
        : node.inputIO === TaskIO.STREAM
          ? [inputs]
          : inputs.length === 0
            ? [[]]
            : (async function* () {
                for await (const item of inputs[0]) yield [item]
              })()

    // the cleanup also fires when the consumer abandons the stream early
    // (e.g. a Take that found its item), so STOP always balances START
    const { value: stream, error } = SafeIterable(results(calls), error =>
      tracking?.({ enum: TrackingEnum.STOP, guid, error })
    )
    yield* stream
    const err = await error
    if (err) throw err
  }

  return { plan, execute }
}
